import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import * as Popover from "@radix-ui/react-popover";

import { usePeopleSearch } from "@lib/logic";
import { Icon } from "@atoms";
import { cn } from "@utils";

import { PeopleSearchResults } from "./components/PeopleSearchResults";

type PeopleSearchProps = {
  className?: string;
};

export const PeopleSearch = ({ className }: PeopleSearchProps) => {
  const { t } = useTranslation();
  const { query, setQuery, status, results } = usePeopleSearch();
  const [isDismissed, setIsDismissed] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);

  const changeQuery = (nextQuery: string) => {
    setIsDismissed(false);
    setQuery(nextQuery);
  };

  return (
    <Popover.Root
      open={status !== "idle" && !isDismissed}
      onOpenChange={isOpen => setIsDismissed(!isOpen)}>
      <Popover.Anchor asChild>
        <div
          ref={fieldRef}
          className={cn("relative", className)}>
          <Icon
            aria-hidden
            icon="search"
            color="currentColor"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-mapiq-black-500"
          />

          <input
            type="search"
            aria-label={t("connection:SearchCoworkers")}
            placeholder={t("connection:SearchCoworkers")}
            value={query}
            className="w-full rounded-lg bg-mapiq-black-75 pb-[7px] pt-[9px] pl-9 pr-9 placeholder:text-mapiq-black-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mapiq-black-800* [&::-webkit-search-cancel-button]:hidden"
            onChange={event => changeQuery(event.target.value)}
            onFocus={() => setIsDismissed(false)}
            onKeyDown={event => {
              if (event.key === "Escape") changeQuery("");
            }}
          />

          {query && (
            <button
              type="button"
              aria-label={t("hereAndNow:ClearSearch")}
              className="absolute right-2 top-1/2 flex size-6 -translate-y-1/2 items-center justify-center rounded-full text-mapiq-black-500 hover:bg-black/[0.05] focus-visible:outline focus-visible:outline-2 focus-visible:outline-mapiq-black-900"
              onClick={() => changeQuery("")}>
              <Icon
                aria-hidden
                icon="close"
                color="currentColor"
                className="size-3"
              />
            </button>
          )}
        </div>
      </Popover.Anchor>

      {/* Not in a portal, so Tab goes from the field to the results */}
      <Popover.Content
        align="start"
        sideOffset={8}
        className="z-50 flex max-h-[min(400px,var(--radix-popover-content-available-height,400px))] w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden rounded-lg bg-white shadow-mapiq-overlay"
        onOpenAutoFocus={event => event.preventDefault()}
        onInteractOutside={event => {
          if (event.target instanceof Node && fieldRef.current?.contains(event.target)) event.preventDefault();
        }}>
        <PeopleSearchResults
          status={status}
          results={results}
        />
      </Popover.Content>
    </Popover.Root>
  );
};
